import { ModalBase } from "@creditornot/cb-modal";
import { BREAKPOINT_MOBILE_MAX } from "@creditornot/cb-modal";
import { jsx, jsxs } from "react/jsx-runtime";
import { styled } from "@linaria/react";
import { forwardRef, useMemo, useId, createElement } from "react";
import { typographyClassName } from "@creditornot/cb-ingredients";
import { createCssVars, clsx, assignInlineCssVars } from "@creditornot/cb-utils";
import { IconButton } from "@creditornot/cb-button";
import "./style.css";
const cssVars$2 = createCssVars(["padding"]);
const Root$2 = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_ModalContent_Root_r11z",
  propsAsIs: false
});
const ModalContent = ({
  children,
  className,
  disablePadding,
  style,
  ...rest
}) => /* @__PURE__ */ jsx(Root$2, {
  className: clsx(className, typographyClassName.Body3),
  style: {
    ...assignInlineCssVars(cssVars$2, {
      padding: disablePadding ? "0px" : void 0
    }),
    ...style
  },
  ...rest,
  children
});
const cssVars$1 = createCssVars(["borderRadius"]);
const Root$1 = /* @__PURE__ */ styled("footer")({
  name: "Root",
  class: "cb_ModalFooter_Root_rdxv",
  propsAsIs: false
});
const ModalFooter = ({
  children,
  className,
  roundedCorners,
  style,
  ...rest
}) => /* @__PURE__ */ jsx(Root$1, {
  className: clsx(className, typographyClassName.Body3),
  style: {
    ...(roundedCorners !== void 0 && assignInlineCssVars(cssVars$1, {
      borderRadius: roundedCorners ? "16px" : "0px"
    })),
    ...style
  },
  ...rest,
  children
});
const SvgCross = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M2.44 19.44a1.5 1.5 0 002.121 2.121l7.262-7.26a.25.25 0 01.354 0l7.262 7.262a1.5 1.5 0 102.122-2.12l-7.26-7.266a.25.25 0 010-.354l7.262-7.262a1.5 1.5 0 00-2.124-2.12L12.177 9.7a.25.25 0 01-.354 0L4.561 2.44A1.5 1.5 0 002.44 4.56L9.7 11.823a.25.25 0 010 .354L2.44 19.44z" }) }));
const cssVars = createCssVars(["borderRadius"]);
const Root = /* @__PURE__ */ styled("header")({
  name: "Root",
  class: "cb_ModalHeader_Root_rth6",
  propsAsIs: false
});
const LeftColumn = /* @__PURE__ */ styled("div")({
  name: "LeftColumn",
  class: "cb_ModalHeader_LeftColumn_l1es",
  propsAsIs: false
});
const RightColumn = /* @__PURE__ */ styled("div")({
  name: "RightColumn",
  class: "cb_ModalHeader_RightColumn_r1g8",
  propsAsIs: false
});
const Heading = /* @__PURE__ */ styled("h2")({
  name: "Heading",
  class: "cb_ModalHeader_Heading_h1hv",
  propsAsIs: false
});
const ModalHeader = ({
  children,
  closeButtonAriaLabel = "Close dialog",
  closeDisabled = false,
  headingId,
  onCloseClick,
  roundedCorners,
  style,
  ...rest
}) => /* @__PURE__ */ jsxs(Root, {
  style: {
    ...(roundedCorners !== void 0 && assignInlineCssVars(cssVars, {
      borderRadius: roundedCorners ? "16px" : "0px"
    })),
    ...style
  },
  ...rest,
  children: [/* @__PURE__ */ jsx(LeftColumn, {
    children: /* @__PURE__ */ jsx(Heading, {
      className: typographyClassName.Heading6,
      id: headingId,
      children
    })
  }), /* @__PURE__ */ jsx(RightColumn, {
    children: onCloseClick && /* @__PURE__ */ jsx(IconButton, {
      alignment: "boundingBox",
      "aria-label": closeButtonAriaLabel,
      background: "solid",
      disabled: closeDisabled,
      onClick: onCloseClick,
      size: "small",
      variant: "neutral",
      children: /* @__PURE__ */ jsx(SvgCross, {})
    })
  })]
});
const sizePresets = {
  "centered:small": {
    type: "centered",
    width: "100%",
    maxWidth: "320px",
    maxHeight: "800px"
  },
  "centered:medium": {
    type: "centered",
    width: "100%",
    maxWidth: "480px",
    maxHeight: "1000px"
  },
  "centered:large": {
    type: "centered",
    width: "100%",
    maxWidth: "640px",
    maxHeight: "1200px"
  },
  "centered:extraLarge": {
    type: "centered",
    width: "100%",
    maxWidth: "800px",
    maxHeight: "1600px"
  },
  "sideStartSheet:small": {
    type: "sideStartSheet",
    width: "100%",
    maxWidth: "280px"
  },
  "sideStartSheet:medium": {
    type: "sideStartSheet",
    width: "100%",
    maxWidth: "480px"
  },
  "sideStartSheet:large": {
    type: "sideStartSheet",
    width: "100%",
    maxWidth: "640px"
  },
  "sideStartSheet:extraLarge": {
    type: "sideStartSheet",
    width: "100%",
    maxWidth: "800px"
  },
  "sideEndSheet:small": {
    type: "sideEndSheet",
    width: "100%",
    maxWidth: "280px"
  },
  "sideEndSheet:medium": {
    type: "sideEndSheet",
    width: "100%",
    maxWidth: "480px"
  },
  "sideEndSheet:large": {
    type: "sideEndSheet",
    width: "100%",
    maxWidth: "640px"
  },
  "sideEndSheet:extraLarge": {
    type: "sideEndSheet",
    width: "100%",
    maxWidth: "800px"
  },
  "bottomSheet:shrinked": {
    type: "bottomSheet",
    maxWidth: "800px"
  },
  "bottomSheet:stretched": {
    type: "bottomSheet",
    maxWidth: "800px",
    height: "100%"
  },
  fullScreen: {
    type: "fullScreen"
  }
};
const warnings = /* @__PURE__ */ new Set();
function warnOnce(...args) {
  if (process.env.NODE_ENV !== "production") {
    const key = args.join(" ");
    if (warnings.has(key)) {
      return;
    }
    warnings.add(key);
    console.warn(...args);
  }
}
const _exp = () => ModalBase;
const StyledModalBase = /* @__PURE__ */ styled(_exp())({
  name: "StyledModalBase",
  class: "cb_Modal_StyledModalBase_sy5q",
  propsAsIs: true
});
const Modal = ({
  "aria-labelledby": ariaLabelledby,
  children,
  className,
  closeButtonAriaLabel = "Close dialog",
  ContentComponent = ModalContent,
  contentStyle,
  disableContentPadding = false,
  footer,
  FooterComponent = ModalFooter,
  footerStyle,
  HeaderComponent = ModalHeader,
  headerStyle,
  headingId: headingIdProp,
  onDismiss,
  scroll = "content",
  sizeConfig,
  sizePreset,
  title,
  ...rest
}) => {
  if (sizeConfig && sizePreset) {
    warnOnce("Warning (`<Modal />`): you have provided both `sizeConfig` and `sizePreset`. `sizePreset` takes precedence over `sizeConfig`.");
  }
  const resolvedSizeConfig = useMemo(() => {
    if (!sizePreset) {
      return sizeConfig;
    }
    if (typeof sizePreset === "string") {
      return sizePresets[sizePreset];
    }
    return {
      mobile: sizePresets[sizePreset.mobile],
      desktop: sizePresets[sizePreset.desktop]
    };
  }, [sizeConfig, sizePreset]);
  const headingIdAuto = useId();
  const headingId = headingIdProp ?? headingIdAuto;
  return (
    /* @__PURE__ */ jsxs(StyledModalBase, {
      "aria-labelledby": ariaLabelledby ?? (HeaderComponent ? headingId : void 0),
      className: clsx(className, typographyClassName.Body3),
      disableModalContentScroll: scroll === "content",
      onDismiss,
      ...rest,
      sizeConfig: resolvedSizeConfig,
      children: [!!HeaderComponent && !!title && /* @__PURE__ */ jsx(HeaderComponent, {
        closeButtonAriaLabel,
        headingId,
        onCloseClick: () => onDismiss == null ? void 0 : onDismiss(),
        style: headerStyle,
        children: typeof title === "string" ? title : createElement(title, {
          id: headingId
        })
      }), !!ContentComponent ? /* @__PURE__ */ jsx(ContentComponent, {
        "data-scroll": scroll,
        disablePadding: disableContentPadding,
        style: {
          ...contentStyle,
          ...(scroll === "container" && {
            overflowY: "visible"
          })
        },
        children
      }) : children, !!FooterComponent && !!footer && /* @__PURE__ */ jsx(FooterComponent, {
        style: footerStyle,
        children: footer
      })]
    })
  );
};
export {
  BREAKPOINT_MOBILE_MAX,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader
};
//# sourceMappingURL=index.js.map
